'use client';

/**
 * EditorialLegacyLayout — applies the `se3` editorial reskin to LEGACY
 * (non-Builder) offering pages whose body is authored in Froala, not Builder.io
 * (e.g. AtomBeam). Paige QA, 2026-07-09.
 *
 * The SE3 editorial treatment previously lived ONLY on the Builder.io path
 * (`BuilderLayoutClient` → `EditorialOfferingHero` + editorially-styled Builder
 * body). Builder-less offerings dropped through `shouldRenderFallback` to the
 * plain classic `<OfferingComponent>` — no editorial treatment at all.
 *
 * This shell renders the REAL editorial chrome around the legacy body:
 *   1. `<EditorialOfferingHero>` — the same data-driven editorial hero the
 *      Builder path uses (gold eyebrow, eligibility badges, serif italic
 *      headline, cover card, "Raised" figure, editorial Get Equity CTA).
 *   2. the legacy `<OfferingComponent>` (passed as `children`) rendered with
 *      `renderHero={false}` so its classic hero isn't duplicated — its Froala
 *      story body + invest sidebar + terms/team/rewards remain intact (the
 *      invest flow, a platform critical flow, is untouched).
 *   3. an editorial token + typography layer over the whole thing: cream page,
 *      IBM Plex Serif display headings, teal Froala-story links, and editorial
 *      restyling of the classic invest-sidebar stats (serif teal figures, gold
 *      uppercase labels, gold-topped card) to match the design.
 *
 * The Froala body content itself (the issuer's authored HTML) can't be
 * restructured into the design's bespoke numbered cards without Builder
 * authoring — the chrome matches; the body is the issuer's real content,
 * editorially skinned.
 *
 * Gating: only mounted when the effective offering variant is `se3` (see
 * `BuilderLayoutServer` fallback branch). Control / every other variant renders
 * the classic `<OfferingComponent>` bare, byte-for-byte unchanged.
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import { ErrorBoundary } from 'react-error-boundary';

import { EDITORIAL_TOKEN_VARS } from '../designTokens';
import EditorialOfferingHero from './EditorialOfferingHero';
import EditorialLegacyNav from './EditorialLegacyNav';
// Reuse the shared editorial accents so the legacy reskin's sidebar matches the
// rest of the editorial sections exactly: GOLD (#856528, the contrast-safe gold
// used by every editorial eyebrow) and TEAL (#185C65 = se3Colors.seDark).
import { ACCENT_GOLD as GOLD, TEAL } from './editorialSection';

// The editorial page is cream (#FCF9F5) end-to-end in the design. The app's
// global <body> background is a cooler off-white (#FAFDFA) that seams against
// the cream content (top-nav strip / overscroll). Matches se3Colors.cream /
// EDITORIAL_TOKEN_VARS --bld-background.
const CREAM = '#FCF9F5';

const EditorialLegacyLayout = ({ children, offeringData }) => {
  // Paint the whole page cream while the editorial reskin is mounted so there's
  // no #FAFDFA seam behind the content. The global desktop rule
  // (ProviderWrapper/GlobalStyles) sets `body { background: #fafdfa !important }`,
  // so we must set our override with `important` priority to win, and restore
  // the previous inline value on unmount.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const { body } = document;
    const prevValue = body.style.getPropertyValue('background-color');
    const prevPriority = body.style.getPropertyPriority('background-color');
    body.style.setProperty('background-color', CREAM, 'important');
    return () => {
      body.style.setProperty('background-color', prevValue, prevPriority);
    };
  }, []);

  return (
    <>
      {/* Match the Builder editorial path: load the serif display face used by
          the `--bld-font-display` token so headings restyle immediately.
          `<OfferingComponent>` (the child) fires its own
          `PerIssuerExperimentExposure`, so the shell must not duplicate it. */}
      <link
        rel="stylesheet"
        href="https://fonts.googleapis.com/css2?family=IBM+Plex+Serif:ital,wght@0,400;0,500;0,600;1,400;1,500&display=swap"
      />
      <Box
        data-cy="editorial-legacy-layout"
        data-builder-editorial-reskin="true"
        style={EDITORIAL_TOKEN_VARS}
        sx={{
          backgroundColor: 'var(--bld-background, #FCF9F5)',
          minHeight: '100vh',
          color: 'var(--bld-text, #221E19)',
          '& sup': {
            fontSize: '0.62em',
            lineHeight: 0,
            verticalAlign: 'baseline',
            position: 'relative',
            top: '-0.5em',
          },
          // Serif display headings — the editorial signature. Italic emphasis
          // picks up the editorial teal to mirror the Builder path.
          '& h1, & h2, & h3': {
            fontFamily: "var(--bld-font-display, 'IBM Plex Serif', Georgia, serif)",
            fontWeight: 500,
            letterSpacing: '-0.01em',
          },
          '& h1': { fontWeight: 400, letterSpacing: '-0.012em' },
          '& h1 em, & h1 i, & h2 em, & h2 i, & h3 em, & h3 i': {
            fontStyle: 'italic',
            color: 'var(--bld-primary, #0C6F64)',
          },
          '& h4, & h5, & h6': {
            fontFamily: "var(--bld-font-display, 'IBM Plex Serif', Georgia, serif)",
          },
          // Editorial teal links inside the Froala story body only — leave nav,
          // buttons, and the invest sidebar controls on their own palette.
          '& [data-cy="story-section"] a[href]': {
            color: 'var(--bld-primary, #0C6F64) !important',
            textDecorationColor: 'currentColor !important',
          },
          // Editorial restyle of the classic invest-sidebar stats card
          // (CompanyComps) so the numbers/labels match the design's sidebar:
          // serif teal figures, gold uppercase labels.
          '& [data-cy^="company-comp-"][data-cy$="-amount"], & [data-cy^="company-comp-"][data-cy$="-count"], & [data-cy^="company-comp-"][data-cy$="-cap-amount"]':
            {
              fontFamily: "var(--bld-font-display, 'IBM Plex Serif', Georgia, serif) !important",
              color: `${TEAL} !important`,
              fontWeight: '500 !important',
            },
          '& [data-cy^="company-comp-"][data-cy$="-label"]': {
            fontFamily: "var(--bld-font-body, 'IBM Plex Sans', system-ui, sans-serif) !important",
            fontSize: '11px !important',
            fontWeight: '600 !important',
            lineHeight: '1.3 !important',
            color: `${GOLD} !important`,
            letterSpacing: '0.16em',
            textTransform: 'uppercase',
          },
          // Align the classic invest CTA ("Get Equity" — the floating sidebar +
          // mobile sticky button) with the Builder SE3 EditorialInvestSidebar
          // CTA font (Paige QA): IBM Plex Sans, uppercase, 0.14em, weight 600.
          // Font only — the button keeps its teal fill / layout.
          '& [data-cy="campaign-page-invest-cta-desktop-button"], & [data-cy="campaign-page-invest-cta-mobile-button"]':
            {
              fontFamily: "var(--bld-font-body, 'IBM Plex Sans', system-ui, sans-serif) !important",
              textTransform: 'uppercase',
              letterSpacing: '0.14em',
              fontWeight: 600,
              // Design Get Equity button is taller (Hannah QA: "too short").
              minHeight: '52px',
            },
          // Waitlist status badge: preserve the classic tooltip but give the
          // status itself a quiet editorial pill. Avoid forcing `fill` on the
          // icon: that turns the information glyph into a visually heavy solid
          // gold circle.
          '& [data-cy="campaign-page-hero-waitlist-badge"]': {
            backgroundColor: 'transparent !important',
            border: `1px solid rgba(133,101,40,0.52)`,
            borderRadius: '999px !important',
            display: 'inline-flex !important',
            alignItems: 'center',
            minHeight: '46px',
            marginBottom: '12px !important',
            '& .MuiTypography-root': {
              color: `${GOLD} !important`,
              fontFamily: "var(--bld-font-body, 'IBM Plex Sans', system-ui, sans-serif)",
              fontSize: '14px',
              fontWeight: 600,
              letterSpacing: '0.16em',
              padding: '8px 14px',
            },
            '& .MuiIconButton-root': {
              color: `${GOLD} !important`,
              height: '18px',
              marginLeft: '0 !important',
              marginRight: '12px',
              padding: 0,
              width: '18px',
            },
            '& .MuiSvgIcon-root': {
              color: `${GOLD} !important`,
              fill: 'none !important',
              height: '16px',
              stroke: 'currentColor',
              width: '16px',
            },
          },
          // Invest sidebar card: the classic CompanyComps box has a diagonal
          // white→grey gradient; the design is a FLAT white card with a gold TOP
          // border + soft shadow (Hannah QA: "no gradient in the design").
          '& [data-cy="invest-now-comp-box"]': {
            background: 'var(--bld-white, #FFFFFF) !important',
            backgroundImage: 'none !important',
            borderTop: `2px solid ${GOLD}`,
            borderRadius: '16px',
            boxShadow: '0 10px 26px rgba(11,41,45,0.06)',
          },
          // "Have more questions?" (RequestLiveStreamCard) — flatten the diagonal
          // gradient to a clean white editorial card (Hannah QA: no gradients).
          '& [data-cy="request-live-stream-card"]': {
            background: 'var(--bld-white, #FFFFFF) !important',
            backgroundImage: 'none !important',
            borderRadius: '16px',
          },
          // The classic invest sidebar column (`.right`) is `display:none` below
          // the `lg` (1200px) breakpoint, while the mobile sticky invest bar only
          // shows below `md` (900px) — so 900–1200px has NO Get Equity column
          // (Hannah QA). Reveal the sidebar column in that gap band, scoped to
          // the editorial page (control's classic layout is untouched).
          '@media (min-width: 900px) and (max-width: 1199.95px)': {
            '& .MuiGrid-item.right': {
              display: 'block !important',
            },
          },
          // "Most Momentum" card (classic CriteriaBadge) has a diagonal cream
          // gradient; flatten to a clean editorial card (Hannah QA).
          '& [data-cy="offering-momentum-card"]': {
            background: 'var(--bld-white, #FFFFFF) !important',
            backgroundImage: 'none !important',
            border: '1px solid var(--bld-border, #E8E1D6)',
            borderRadius: '16px',
          },
        }}
      >
        <ErrorBoundary FallbackComponent={() => null}>
          <EditorialLegacyNav />
        </ErrorBoundary>
        <ErrorBoundary FallbackComponent={() => null}>
          <EditorialOfferingHero offeringData={offeringData} />
        </ErrorBoundary>
        {children}
      </Box>
    </>
  );
};

EditorialLegacyLayout.propTypes = {
  children: PropTypes.node,
  offeringData: PropTypes.object,
};

export default EditorialLegacyLayout;
